import { UseTicketListParams, useUpdateTodoMutation } from 'hooks/business/chat/useTicketMutations';
import { useCallback } from 'react';
import { PureRelatedItems } from './PureRelatedItems';
import { PureOnlyRelatedItems } from './PureOnlyRelatedItems';

/** 将订单商品项转为 Todo.RelatedSku */
function tradeProductToRelatedSku(
  item: Conversation.Trade.Product & { skuId?: string },
): Todo.RelatedSku {
  return {
    itemId: item.externalItemId,
    skuId: item.skuId ?? item.externalSkuId ?? '',
    skuName: item.skuName ?? '',
    sellerSku: item.sellerSku ?? '',
    quantity: item.quantity ?? 1,
    itemName: item.itemName ?? '',
  };
}

interface RelatedItemsProps {
  ticket: Todo.TodoInstance;
  readonly?: boolean;
  listParams: UseTicketListParams | null;
}

export const RelatedItems = (props: RelatedItemsProps) => {
  const { ticket, listParams, readonly = false } = props;

  const canEdit = Boolean(listParams && !readonly);
  const { relatedTradeId, platform, storeId, relatedSku } = ticket;

  const externalBuyerId = listParams?.tradeBuyerId ?? '';
  const ready = Boolean(listParams && platform !== 'FACEBOOK');

  const updateTodoMutation = useUpdateTodoMutation(listParams);
  const { id } = ticket;
  const getRelatedSkuKey = useCallback((item: Pick<Todo.RelatedSku, 'itemId' | 'skuId'>) => {
    return `${item.itemId}___${item.skuId || ''}`;
  }, []);

  const handleSelect = useCallback(
    (item: Conversation.Trade.Product, selected: boolean) => {
      const nextItem = tradeProductToRelatedSku(
        item as Conversation.Trade.Product & { skuId?: string },
      );
      const nextItemKey = getRelatedSkuKey(nextItem);
      let nextRelatedSku = relatedSku;

      if (selected) {
        const exists = relatedSku.some((sku) => getRelatedSkuKey(sku) === nextItemKey);
        nextRelatedSku = exists ? relatedSku : [...relatedSku, nextItem];
      } else {
        nextRelatedSku = relatedSku.filter((sku) => getRelatedSkuKey(sku) !== nextItemKey);
      }

      updateTodoMutation.mutate({ id, relatedSku: nextRelatedSku });
    },
    [getRelatedSkuKey, id, relatedSku, updateTodoMutation],
  );

  const handleOnlyRelatedItemsChange = useCallback(
    (next: Todo.RelatedSku[]) => {
      updateTodoMutation.mutate({ id, relatedSku: next });
    },
    [id, updateTodoMutation],
  );

  const handleQuantityChange = useCallback(
    (index: number, quantity: number) => {
      const next = [...relatedSku];
      if (next[index] == null) return;
      next[index] = { ...next[index], quantity };
      updateTodoMutation.mutate({ id, relatedSku: next });
    },
    [id, relatedSku, updateTodoMutation],
  );
  const handleClear = useCallback(() => {
    if (!relatedSku.length) return;
    updateTodoMutation.mutate({ id, relatedSku: [] });
  }, [id, relatedSku.length, updateTodoMutation]);

  if (!relatedTradeId) {
    return (
      <PureOnlyRelatedItems
        storeId={storeId}
        relatedSku={relatedSku}
        canEdit={canEdit && !readonly}
        onChange={handleOnlyRelatedItemsChange}
        onQuantityChange={handleQuantityChange}
        platform={platform}
      />
    );
  }

  return (
    <PureRelatedItems
      canEdit={canEdit}
      relatedTradeId={relatedTradeId}
      platform={platform}
      storeId={storeId}
      relatedSku={relatedSku}
      externalBuyerId={externalBuyerId}
      ready={ready && !readonly}
      onSelect={handleSelect}
      onQuantityChange={handleQuantityChange}
      onClear={handleClear}
    />
  );
};
